import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../utils/apiClient';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Loader2, 
  Target, 
  Brain, 
  CheckCircle2, 
  Plus, 
  Trash2, 
  Globe, 
  Package, 
  Users, 
  ArrowRight, 
  ArrowLeft,
  Sparkles,
  DollarSign,
  Building2,
  HelpCircle,
  History,
  Calendar,
  Eye,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const ICPGeneratorNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isCreateNew = location.state?.createNew === true;
  const editBusiness = location.state?.editBusiness || null;
  const isEditMode = !!editBusiness;

  // Main tab
  const [activeTab, setActiveTab] = useState('generate');
  
  // Wizard step
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Loading states
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(!isCreateNew && !isEditMode);
  const [analyzingWebsite, setAnalyzingWebsite] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [loadingPlatforms, setLoadingPlatforms] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Results
  const [icp, setIcp] = useState(null);
  const [businessId, setBusinessId] = useState(editBusiness?.id || null);
  const [platformRecommendations, setPlatformRecommendations] = useState(null);
  const [suggestedAudiences, setSuggestedAudiences] = useState([]);
  const [showAudienceHelper, setShowAudienceHelper] = useState(false);
  
  // History
  const [icpHistory, setIcpHistory] = useState([]);
  const [selectedHistoryIcp, setSelectedHistoryIcp] = useState(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Form data - Step 1: Business Info
  const [businessName, setBusinessName] = useState(editBusiness?.business_name || '');
  const [website, setWebsite] = useState(editBusiness?.website || '');
  const [businessDescription, setBusinessDescription] = useState(editBusiness?.business_description || '');
  const [industry, setIndustry] = useState(editBusiness?.industry || '');

  // Form data - Step 2: Products/Services (multiple)
  const [products, setProducts] = useState(
    editBusiness?.product_service 
      ? [{ id: 1, name: '', description: editBusiness.product_service, price: editBusiness.price || '' }]
      : [{ id: 1, name: '', description: '', price: '' }]
  );

  // Form data - Step 3: Target Audience
  const [targetAudience, setTargetAudience] = useState(editBusiness?.target_audience || '');

  // Load existing ICP on mount (only if not creating new and not editing)
  useEffect(() => {
    if (!isCreateNew && !isEditMode) {
      loadExistingICP();
    }
  }, [isCreateNew, isEditMode]);

  const loadExistingICP = async () => {
    try {
      setLoadingExisting(true);
      let response = await apiClient.get('/user/active-business');

      if (!response.data.business) {
        await apiClient.post('/user/auto-setup-active-business');
        response = await apiClient.get('/user/active-business');
      }

      if (response.data.business && response.data.icp) {
        setIcp(response.data.icp);
        const bizId = response.data.business.id;
        setBusinessId(bizId);

        // Pre-fill form
        setBusinessName(response.data.business.business_name || '');
        setWebsite(response.data.business.website || '');
        setBusinessDescription(response.data.business.business_description || '');
        setIndustry(response.data.business.industry || '');
        setTargetAudience(response.data.business.target_audience || '');

        // Handle products
        if (response.data.business.product_service) {
          setProducts([{
            id: 1,
            name: '',
            description: response.data.business.product_service,
            price: response.data.business.price || ''
          }]);
        }

        toast.success('ICP de tu empresa activa cargado');

        // Load platform recommendations
        try {
          const platformsResponse = await apiClient.get(`/platforms/recommendations/${bizId}`);
          setPlatformRecommendations(platformsResponse.data);
        } catch (error) {
          console.log('No saved platforms found');
        }
      }
    } catch (error) {
      console.error('Error loading existing ICP:', error);
    } finally {
      setLoadingExisting(false);
    }
  };

  // Load ICP history
  const loadIcpHistory = async (bizId) => {
    if (!bizId) return;
    try {
      setLoadingHistory(true);
      const response = await apiClient.get(`/icp/history/${bizId}`);
      setIcpHistory(response.data || []);
    } catch (error) {
      console.error('Error loading ICP history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load history when switching to history tab
  useEffect(() => {
    if (activeTab === 'history' && businessId) {
      loadIcpHistory(businessId);
    }
  }, [activeTab, businessId]);

  // Product handlers
  const addProduct = () => {
    const newId = Math.max(...products.map(p => p.id)) + 1;
    setProducts([...products, { id: newId, name: '', description: '', price: '' }]);
  };

  const removeProduct = (id) => {
    if (products.length > 1) {
      setProducts(products.filter(p => p.id !== id));
    }
  };

  const updateProduct = (id, field, value) => {
    setProducts(products.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  // Website analysis
  const analyzeWebsite = async () => {
    if (!website) {
      toast.error('Ingresa primero una URL');
      return;
    }

    try {
      setAnalyzingWebsite(true);
      const response = await apiClient.post(
        `/icp/analyze-website?website_url=${encodeURIComponent(website)}`
      );

      const data = response.data;

      // Auto-fill fields
      if (data.business_description) setBusinessDescription(data.business_description);
      if (data.industry) setIndustry(data.industry);
      if (data.target_audience) setTargetAudience(data.target_audience);
      
      // Update first product with extracted info
      if (data.product_service || data.price_range) {
        setProducts([{
          id: 1,
          name: '',
          description: data.product_service || '',
          price: data.price_range !== 'No especificado' ? data.price_range : ''
        }]);
      }

      toast.success('¡Website analizada! Revisa y completa la información');
    } catch (error) {
      console.error('Error analyzing website:', error);
      toast.error('Error al analizar el website');
    } finally {
      setAnalyzingWebsite(false);
    }
  };

  // Get audience suggestions
  const getSuggestedAudiences = async () => {
    const mainProduct = products[0];
    if (!businessDescription || !mainProduct.description) {
      toast.error('Completa primero la descripción del negocio y al menos un producto');
      return;
    }

    try {
      setLoadingSuggestions(true);
      const params = new URLSearchParams({
        business_description: businessDescription,
        product_service: products.map(p => p.description).join('; '),
        price: products.map(p => p.price).filter(p => p).join(', ') || 'Variable',
      });

      if (industry) {
        params.append('industry', industry);
      }

      const response = await apiClient.post(`/icp/suggest-audience?${params.toString()}`);
      setSuggestedAudiences(response.data.audiences || []);
      setShowAudienceHelper(true);
    } catch (error) {
      console.error('Error getting suggestions:', error);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const selectAudience = (audience) => {
    let descriptionText = audience.description;
    if (typeof descriptionText === 'object') {
      descriptionText = Object.entries(descriptionText)
        .map(([key, value]) => `${key.replace('_', ' ')}: ${value}`)
        .join('. ');
    }
    setTargetAudience(descriptionText);
    setShowAudienceHelper(false);
    toast.success('Público objetivo seleccionado');
  };

  // Validation
  const canProceedStep1 = businessName.trim() && businessDescription.trim();
  const canProceedStep2 = products.some(p => p.description.trim() && p.price.trim());
  const canProceedStep3 = targetAudience.trim();

  // Navigation
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Submit
  const handleSubmit = async () => {
    if (!canProceedStep1 || !canProceedStep2 || !canProceedStep3) {
      toast.error('Por favor completa todos los campos requeridos');
      return;
    }

    try {
      setLoading(true);

      // Combine products into single string for API compatibility
      const productServiceText = products
        .filter(p => p.description.trim())
        .map(p => {
          let text = p.description;
          if (p.name) text = `${p.name}: ${text}`;
          return text;
        })
        .join('\n\n');

      const priceText = products
        .filter(p => p.price.trim())
        .map(p => {
          if (p.name) return `${p.name}: ${p.price}`;
          return p.price;
        })
        .join(' | ');

      const formData = {
        business_name: businessName,
        business_description: businessDescription,
        product_service: productServiceText,
        price: priceText,
        target_audience: targetAudience,
        industry: industry,
        website: website,
        chat_history: ''
      };

      let response;
      let generatedBusinessId;

      if (isEditMode && businessId) {
        // Update existing business
        formData.business_id = businessId;
        response = await apiClient.post('/icp/generate', formData);
        generatedBusinessId = response.data.business_id;
        toast.success('¡Negocio actualizado exitosamente!');
      } else {
        // Create new business
        response = await apiClient.post('/icp/generate', formData);
        generatedBusinessId = response.data.business_id;
        toast.success('¡ICP generado exitosamente!');
      }

      setIcp(response.data);
      setBusinessId(generatedBusinessId);

      // Fetch platform recommendations
      fetchPlatformRecommendations(generatedBusinessId);

      // Move to results view
      setCurrentStep(4);
    } catch (error) {
      console.error('Error generating ICP:', error);
      toast.error('Error al generar ICP');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlatformRecommendations = async (bizId) => {
    try {
      setLoadingPlatforms(true);
      const response = await apiClient.get(`/platforms/recommend?business_id=${bizId}`);
      setPlatformRecommendations(response.data);
    } catch (error) {
      console.error('Error fetching platform recommendations:', error);
    } finally {
      setLoadingPlatforms(false);
    }
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step, index) => (
        <React.Fragment key={step}>
          <div 
            className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold transition-all ${
              currentStep >= step 
                ? 'bg-blue-600 text-white' 
                : 'bg-slate-200 text-slate-500'
            } ${currentStep === step ? 'ring-4 ring-blue-200' : ''}`}
          >
            {currentStep > step ? <CheckCircle2 className="w-5 h-5" /> : step}
          </div>
          {index < 2 && (
            <div className={`w-16 sm:w-24 h-1 mx-2 rounded ${
              currentStep > step ? 'bg-blue-600' : 'bg-slate-200'
            }`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step 1: Business Info
  const renderStep1 = () => (
    <Card className="border-0 shadow-lg">
      <CardHeader className="text-center pb-2">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Building2 className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-2xl">Información del Negocio</CardTitle>
        <CardDescription>Cuéntanos sobre tu empresa</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-4">
        {/* Website Analysis */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border-2 border-blue-200">
          <Label className="text-blue-900 font-semibold flex items-center gap-2 mb-2">
            <Globe className="w-4 h-4" />
            ¿Tienes sitio web? ¡Analízalo automáticamente!
          </Label>
          <div className="flex gap-2">
            <Input
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="https://tusitio.com"
              type="url"
              className="flex-1 bg-white"
            />
            <Button
              type="button"
              onClick={analyzeWebsite}
              disabled={!website || analyzingWebsite}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {analyzingWebsite ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Brain className="w-4 h-4 mr-2" />
                  Analizar
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-blue-700 mt-2">
            La IA extraerá automáticamente la información de tu web
          </p>
        </div>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-slate-200"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="px-3 bg-white text-sm text-slate-500">o completa manualmente</span>
          </div>
        </div>

        {/* Business Name */}
        <div>
          <Label htmlFor="businessName" className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-500" />
            Nombre del Negocio *
          </Label>
          <Input
            id="businessName"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Ej: Marketing Pro Agency"
            className="mt-1"
          />
        </div>

        {/* Business Description */}
        <div>
          <Label htmlFor="businessDescription">Descripción del Negocio *</Label>
          <Textarea
            id="businessDescription"
            value={businessDescription}
            onChange={(e) => setBusinessDescription(e.target.value)}
            placeholder="¿Qué hace tu negocio? ¿Qué problema resuelve? ¿Cuál es tu propuesta de valor única?"
            rows={4}
            className="mt-1"
          />
          <p className="text-xs text-slate-500 mt-1">
            Sé específico. Esto ayuda a la IA a entender mejor tu negocio.
          </p>
        </div>

        {/* Industry */}
        <div>
          <Label htmlFor="industry">Industria</Label>
          <Input
            id="industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="Ej: Marketing Digital, E-commerce, Coaching, Salud..."
            className="mt-1"
          />
        </div>

        {/* Navigation */}
        <div className="flex justify-end pt-4">
          <Button
            onClick={nextStep}
            disabled={!canProceedStep1}
            className="bg-gradient-to-r from-blue-600 to-cyan-600"
          >
            Siguiente: Productos
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Step 2: Products/Services
  const renderStep2 = () => (
    <Card className="border-0 shadow-lg">
      <CardHeader className="text-center pb-2">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Package className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-2xl">Productos y Servicios</CardTitle>
        <CardDescription>Agrega todos tus productos o servicios</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-4">
        {products.map((product, index) => (
          <div 
            key={product.id} 
            className="p-4 bg-slate-50 rounded-xl border border-slate-200 relative"
          >
            {products.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeProduct(product.id)}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}

            <div className="flex items-center gap-2 mb-3">
              <span className="w-6 h-6 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-semibold">
                {index + 1}
              </span>
              <span className="font-medium text-slate-700">
                {index === 0 ? 'Producto/Servicio Principal' : `Producto/Servicio ${index + 1}`}
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <Label>Nombre (opcional)</Label>
                <Input
                  value={product.name}
                  onChange={(e) => updateProduct(product.id, 'name', e.target.value)}
                  placeholder="Ej: Consultoría Premium"
                  className="mt-1 bg-white"
                />
              </div>

              <div>
                <Label>Descripción *</Label>
                <Textarea
                  value={product.description}
                  onChange={(e) => updateProduct(product.id, 'description', e.target.value)}
                  placeholder="¿Qué incluye? ¿Qué resultados ofrece? ¿Qué lo hace único?"
                  rows={3}
                  className="mt-1 bg-white"
                />
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  Precio *
                </Label>
                <Input
                  value={product.price}
                  onChange={(e) => updateProduct(product.id, 'price', e.target.value)}
                  placeholder="Ej: $500 USD, $100-$500, Desde $299/mes"
                  className="mt-1 bg-white"
                />
              </div>
            </div>
          </div>
        ))}

        {/* Add Product Button */}
        <Button
          type="button"
          variant="outline"
          onClick={addProduct}
          className="w-full border-dashed border-2 border-purple-300 text-purple-600 hover:bg-purple-50"
        >
          <Plus className="w-4 h-4 mr-2" />
          Agregar otro producto/servicio
        </Button>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={prevStep}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Anterior
          </Button>
          <Button
            onClick={nextStep}
            disabled={!canProceedStep2}
            className="bg-gradient-to-r from-purple-600 to-pink-600"
          >
            Siguiente: Público
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Step 3: Target Audience
  const renderStep3 = () => (
    <Card className="border-0 shadow-lg">
      <CardHeader className="text-center pb-2">
        <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Users className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-2xl">Público Objetivo</CardTitle>
        <CardDescription>¿A quién le vendes?</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6 pt-4">
        {/* AI Suggestion */}
        <div className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border-2 border-orange-200">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-orange-900 font-semibold flex items-center gap-2">
              <Sparkles className="w-4 h-4" />
              ¿No estás seguro de tu público?
            </Label>
            <Dialog open={showAudienceHelper} onOpenChange={setShowAudienceHelper}>
              <DialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={getSuggestedAudiences}
                  disabled={loadingSuggestions}
                  className="border-orange-300 text-orange-700 hover:bg-orange-100"
                >
                  {loadingSuggestions ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <HelpCircle className="w-4 h-4 mr-1" />
                      Sugerir públicos
                    </>
                  )}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Opciones de Público Objetivo</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  {loadingSuggestions ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <Loader2 className="w-10 h-10 text-orange-500 animate-spin mb-4" />
                      <p className="text-slate-600 font-medium">Analizando tu negocio...</p>
                      <p className="text-sm text-slate-500 mt-1">Generando opciones de público objetivo</p>
                    </div>
                  ) : suggestedAudiences.length > 0 ? (
                    suggestedAudiences.map((audience, index) => (
                      <div
                        key={index}
                        className="p-4 border rounded-lg hover:border-orange-400 hover:bg-orange-50 cursor-pointer transition-all"
                        onClick={() => selectAudience(audience)}
                      >
                        <h4 className="font-semibold text-slate-900 mb-2">{audience.name}</h4>
                        <p className="text-sm text-slate-600">
                          {typeof audience.description === 'object'
                            ? Object.entries(audience.description)
                                .map(([k, v]) => `${k}: ${v}`)
                                .join(' | ')
                            : audience.description}
                        </p>
                        {audience.reasoning && (
                          <p className="text-xs text-orange-600 mt-2 italic">
                            💡 {audience.reasoning}
                          </p>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-slate-500 py-8">
                      Haz clic en "Sugerir públicos" para obtener recomendaciones basadas en tu negocio
                    </p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <p className="text-xs text-orange-700">
            La IA analizará tu negocio y productos para sugerirte públicos ideales
          </p>
        </div>

        {/* Target Audience Input */}
        <div>
          <Label htmlFor="targetAudience">Descripción del Público Objetivo *</Label>
          <Textarea
            id="targetAudience"
            value={targetAudience}
            onChange={(e) => setTargetAudience(e.target.value)}
            placeholder="Describe a tu cliente ideal: edad, género, ubicación, profesión, intereses, problemas que tiene, qué busca resolver..."
            rows={5}
            className="mt-1"
          />
          <p className="text-xs text-slate-500 mt-1">
            Entre más detallado, mejor será el ICP y el contenido generado.
          </p>
        </div>

        {/* Summary Preview */}
        <div className="p-4 bg-slate-50 rounded-xl">
          <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            Resumen de tu Negocio
          </h4>
          <div className="space-y-2 text-sm">
            <p><span className="font-medium">Negocio:</span> {businessName || '-'}</p>
            <p><span className="font-medium">Industria:</span> {industry || '-'}</p>
            <p><span className="font-medium">Productos:</span> {products.filter(p => p.description).length}</p>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={prevStep}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Anterior
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canProceedStep3 || loading}
            className="bg-gradient-to-r from-orange-600 to-red-600"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {isEditMode ? 'Actualizando...' : 'Generando ICP...'}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {isEditMode ? 'Actualizar Negocio' : 'Generar ICP'}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Step 4: Results
  const renderResults = () => (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">
                {isEditMode ? '¡Negocio Actualizado!' : '¡ICP Generado Exitosamente!'}
              </h2>
              <p className="text-green-100">Tu perfil de cliente ideal está listo</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {icp && (
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-6 h-6 text-blue-600" />
              Perfil de Cliente Ideal (ICP)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {/* Demographics */}
              <AccordionItem value="demographics">
                <AccordionTrigger>👤 Demografía</AccordionTrigger>
                <AccordionContent>
                  <div className="grid sm:grid-cols-2 gap-4 text-sm">
                    {(() => {
                      const d = icp.primary_icp?.demographics || icp.demographics || {};
                      return (
                        <>
                          <div><strong>Edad:</strong> {d.age_range || d.edad || d.rango_edad || 'No especificado'}</div>
                          <div><strong>Género:</strong> {d.gender || d.genero || 'No especificado'}</div>
                          <div><strong>Ubicación:</strong> {d.location || d.ubicacion || 'No especificado'}</div>
                          <div><strong>Nivel educativo:</strong> {d.education_level || d.nivel_educativo || d.educacion || 'No especificado'}</div>
                          <div><strong>Ocupación:</strong> {d.occupation || d.ocupacion || 'No especificado'}</div>
                          <div><strong>Ingresos:</strong> {d.income_level || d.nivel_ingresos || d.ingresos || 'No especificado'}</div>
                        </>
                      );
                    })()}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Psychographics */}
              <AccordionItem value="psychographics">
                <AccordionTrigger>🧠 Psicografía</AccordionTrigger>
                <AccordionContent>
                  {(() => {
                    const p = icp.primary_icp?.psychographics || icp.psychographics || {};
                    const renderList = (data) => {
                      if (!data) return <li>No especificado</li>;
                      if (Array.isArray(data)) {
                        return data.map((v, i) => <li key={i}>{typeof v === 'object' ? JSON.stringify(v) : v}</li>);
                      }
                      if (typeof data === 'object') {
                        return Object.entries(data).map(([k, v], i) => (
                          <li key={i}><strong>{k.replace(/_/g, ' ')}:</strong> {typeof v === 'object' ? JSON.stringify(v) : v}</li>
                        ));
                      }
                      return <li>{data}</li>;
                    };
                    return (
                      <div className="space-y-3 text-sm">
                        <div>
                          <strong>Valores:</strong>
                          <ul className="list-disc list-inside mt-1">
                            {renderList(p.values || p.valores)}
                          </ul>
                        </div>
                        <div>
                          <strong>Intereses:</strong>
                          <ul className="list-disc list-inside mt-1">
                            {renderList(p.interests || p.intereses)}
                          </ul>
                        </div>
                        {(p.lifestyle || p.estilo_vida) && (
                          <div>
                            <strong>Estilo de vida:</strong> {p.lifestyle || p.estilo_vida}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </AccordionContent>
              </AccordionItem>

              {/* Pain Points */}
              <AccordionItem value="pain_points">
                <AccordionTrigger>😣 Puntos de Dolor</AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {(() => {
                      const data = icp.pain_points || icp.primary_icp?.pain_points || icp.puntos_dolor || [];
                      if (Array.isArray(data)) {
                        return data.map((p, i) => <li key={i}>{typeof p === 'object' ? JSON.stringify(p) : p}</li>);
                      }
                      if (typeof data === 'object') {
                        return Object.entries(data).map(([k, v], i) => (
                          <li key={i}><strong>{k.replace(/_/g, ' ')}:</strong> {Array.isArray(v) ? v.join(', ') : v}</li>
                        ));
                      }
                      return <li>{data || 'No especificado'}</li>;
                    })()}
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Goals */}
              <AccordionItem value="goals">
                <AccordionTrigger>🎯 Metas y Aspiraciones</AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 text-sm">
                    {(() => {
                      const data = icp.goals || icp.primary_icp?.goals || icp.desires || icp.metas || icp.objetivos || {};
                      if (Array.isArray(data)) {
                        return (
                          <ul className="list-disc list-inside space-y-1">
                            {data.map((g, i) => <li key={i}>{typeof g === 'object' ? JSON.stringify(g) : g}</li>)}
                          </ul>
                        );
                      }
                      if (typeof data === 'object' && Object.keys(data).length > 0) {
                        return Object.entries(data).map(([key, value], i) => (
                          <div key={i} className="mb-2">
                            <strong className="capitalize">{key.replace(/_/g, ' ')}:</strong>
                            {Array.isArray(value) ? (
                              <ul className="list-disc list-inside ml-4 mt-1">
                                {value.map((v, j) => <li key={j}>{typeof v === 'object' ? JSON.stringify(v) : v}</li>)}
                              </ul>
                            ) : (
                              <span className="ml-1">{typeof value === 'object' ? JSON.stringify(value) : value}</span>
                            )}
                          </div>
                        ));
                      }
                      return <p>No especificado</p>;
                    })()}
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Objections */}
              <AccordionItem value="objections">
                <AccordionTrigger>🤔 Objeciones Comunes</AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {(() => {
                      const data = icp.objections || icp.primary_icp?.objections || icp.objeciones || [];
                      if (Array.isArray(data)) {
                        return data.map((o, i) => <li key={i}>{typeof o === 'object' ? JSON.stringify(o) : o}</li>);
                      }
                      if (typeof data === 'object') {
                        return Object.entries(data).map(([k, v], i) => (
                          <li key={i}><strong>{k.replace(/_/g, ' ')}:</strong> {Array.isArray(v) ? v.join(', ') : v}</li>
                        ));
                      }
                      return <li>{data || 'No especificado'}</li>;
                    })()}
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Buying Triggers */}
              <AccordionItem value="buying_triggers">
                <AccordionTrigger>⚡ Disparadores de Compra</AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {(() => {
                      const data = icp.buying_triggers || icp.primary_icp?.buying_triggers || icp.emotional_triggers || icp.disparadores || [];
                      if (Array.isArray(data)) {
                        return data.map((b, i) => <li key={i}>{typeof b === 'object' ? JSON.stringify(b) : b}</li>);
                      }
                      if (typeof data === 'object') {
                        return Object.entries(data).map(([k, v], i) => (
                          <li key={i}><strong>{k.replace(/_/g, ' ')}:</strong> {Array.isArray(v) ? v.join(', ') : v}</li>
                        ));
                      }
                      return <li>{data || 'No especificado'}</li>;
                    })()}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Button
          onClick={() => navigate('/campaign-generator')}
          className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
        >
          Crear Campaña
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button
          onClick={() => navigate('/content-generator')}
          variant="outline"
          className="flex-1"
        >
          Generar Contenido
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
        <Button
          onClick={() => {
            setCurrentStep(1);
            setIcp(null);
          }}
          variant="ghost"
          className="flex-1"
        >
          Editar Información
        </Button>
      </div>
    </div>
  );

  // Helper to render ICP data (reusable for history)
  const renderIcpData = (icpData) => (
    <Accordion type="single" collapsible className="w-full">
      {/* Demographics */}
      <AccordionItem value="demographics">
        <AccordionTrigger>👤 Demografía</AccordionTrigger>
        <AccordionContent>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            {(() => {
              const d = icpData.primary_icp?.demographics || icpData.demographics || {};
              return (
                <>
                  <div><strong>Edad:</strong> {d.age_range || d.edad || d.rango_edad || 'No especificado'}</div>
                  <div><strong>Género:</strong> {d.gender || d.genero || 'No especificado'}</div>
                  <div><strong>Ubicación:</strong> {d.location || d.ubicacion || 'No especificado'}</div>
                  <div><strong>Nivel educativo:</strong> {d.education_level || d.nivel_educativo || d.educacion || 'No especificado'}</div>
                  <div><strong>Ocupación:</strong> {d.occupation || d.ocupacion || 'No especificado'}</div>
                  <div><strong>Ingresos:</strong> {d.income_level || d.nivel_ingresos || d.ingresos || 'No especificado'}</div>
                </>
              );
            })()}
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Pain Points */}
      <AccordionItem value="pain_points">
        <AccordionTrigger>😣 Puntos de Dolor</AccordionTrigger>
        <AccordionContent>
          <ul className="list-disc list-inside space-y-1 text-sm">
            {(() => {
              const data = icpData.pain_points || icpData.primary_icp?.pain_points || [];
              if (Array.isArray(data)) {
                return data.map((p, i) => <li key={i}>{typeof p === 'object' ? JSON.stringify(p) : p}</li>);
              }
              return <li>{data || 'No especificado'}</li>;
            })()}
          </ul>
        </AccordionContent>
      </AccordionItem>

      {/* Goals */}
      <AccordionItem value="goals">
        <AccordionTrigger>🎯 Metas</AccordionTrigger>
        <AccordionContent>
          <div className="space-y-2 text-sm">
            {(() => {
              const data = icpData.goals || icpData.primary_icp?.goals || icpData.desires || {};
              if (Array.isArray(data)) {
                return (
                  <ul className="list-disc list-inside">
                    {data.map((g, i) => <li key={i}>{typeof g === 'object' ? JSON.stringify(g) : g}</li>)}
                  </ul>
                );
              }
              if (typeof data === 'object' && Object.keys(data).length > 0) {
                return Object.entries(data).map(([key, value], i) => (
                  <div key={i}>
                    <strong className="capitalize">{key.replace(/_/g, ' ')}:</strong>
                    {Array.isArray(value) ? (
                      <ul className="list-disc list-inside ml-4">
                        {value.map((v, j) => <li key={j}>{v}</li>)}
                      </ul>
                    ) : (
                      <span className="ml-1">{value}</span>
                    )}
                  </div>
                ));
              }
              return <p>No especificado</p>;
            })()}
          </div>
        </AccordionContent>
      </AccordionItem>

      {/* Objections */}
      <AccordionItem value="objections">
        <AccordionTrigger>🤔 Objeciones</AccordionTrigger>
        <AccordionContent>
          <ul className="list-disc list-inside space-y-1 text-sm">
            {(() => {
              const data = icpData.objections || icpData.primary_icp?.objections || [];
              if (Array.isArray(data)) {
                return data.map((o, i) => <li key={i}>{typeof o === 'object' ? JSON.stringify(o) : o}</li>);
              }
              return <li>{data || 'No especificado'}</li>;
            })()}
          </ul>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );

  // History tab
  const renderHistoryTab = () => (
    <div className="space-y-6">
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="w-6 h-6 text-blue-600" />
            Historial de ICPs
          </CardTitle>
          <CardDescription>
            {icpHistory.length} ICP{icpHistory.length !== 1 ? 's' : ''} generado{icpHistory.length !== 1 ? 's' : ''} para este negocio
          </CardDescription>
        </CardHeader>
      </Card>

      {loadingHistory ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : !businessId ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="text-center py-12">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">Primero genera un ICP para ver el historial</p>
          </CardContent>
        </Card>
      ) : icpHistory.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="text-center py-12">
            <Target className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">No hay ICPs generados todavía</p>
            <Button onClick={() => setActiveTab('generate')} className="mt-4">
              Generar Primer ICP
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {icpHistory.map((historyIcp, index) => (
            <Card 
              key={historyIcp.id || index}
              className="border-0 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
              onClick={() => {
                setSelectedHistoryIcp(historyIcp);
                setShowHistoryModal(true);
              }}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(historyIcp.created_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric'
                    })}
                  </span>
                </div>
                
                <h3 className="font-semibold text-slate-900 mb-2">
                  ICP #{icpHistory.length - index}
                </h3>
                
                <div className="text-xs text-slate-600 space-y-1">
                  {historyIcp.primary_icp?.demographics?.occupation && (
                    <p>👤 {historyIcp.primary_icp.demographics.occupation}</p>
                  )}
                  {(historyIcp.pain_points?.length || historyIcp.primary_icp?.pain_points?.length) && (
                    <p>😣 {(historyIcp.pain_points || historyIcp.primary_icp?.pain_points)?.length} puntos de dolor</p>
                  )}
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full mt-3 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Ver Detalles
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* History Detail Modal */}
      {showHistoryModal && selectedHistoryIcp && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowHistoryModal(false)}>
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Target className="w-6 h-6" />
                    Detalle del ICP
                  </h2>
                  <p className="text-blue-100 text-sm mt-1 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    {new Date(selectedHistoryIcp.created_at).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20 rounded-full"
                  onClick={() => setShowHistoryModal(false)}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {renderIcpData(selectedHistoryIcp)}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Main render
  if (loadingExisting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-4">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Arquitecto de ICP</h1>
          <p className="text-sm text-slate-600 mt-1">Crea el perfil completo de tu cliente ideal</p>
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="bg-white/80 border-b border-slate-200 px-4 sm:px-6 py-4">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => window.location.href = '/dashboard'}
            className="p-2 hover:bg-slate-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-600" />
          </button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
              {isEditMode ? 'Editar Negocio' : 'Arquitecto de ICP'}
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              {isEditMode 
                ? `Editando: ${editBusiness?.business_name || 'Negocio'}` 
                : 'Crea el perfil completo de tu cliente ideal'}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-6">
            <TabsTrigger value="generate">
              <Sparkles className="w-4 h-4 mr-2" />
              Generar
            </TabsTrigger>
            <TabsTrigger value="history">
              <History className="w-4 h-4 mr-2" />
              Historial ({icpHistory.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="generate">
            <div className="max-w-2xl mx-auto">
              {currentStep <= 3 && renderStepIndicator()}

              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderResults()}
            </div>
          </TabsContent>

          <TabsContent value="history">
            {renderHistoryTab()}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ICPGeneratorNew;
